import {skipToken} from '@reduxjs/toolkit/query';
import {addYears, endOfYear, startOfYear} from 'date-fns';
import {
  Button,
  ButtonGroup,
  closeDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useGetBranchListQuery} from '@dms/api/core';
import {
  useCreateCustomClosingDayMutation,
  useGetClosingDayQuery,
  useUpdateCustomClosingDayMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {getOptionsFromBranches, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {getApiDateString, parseDate} from 'shared/web';

import {CLOSING_DAY_DIALOG_ID, CLOSING_DAY_GRID_CODE} from './constants';
import {CustomClosingDayFormValues, customClosingDayFormSchema} from './validations';

const handleClose = () => closeDialog(CLOSING_DAY_DIALOG_ID);

const NOW = parseDate(Date.now());
const MIN_CLOSING_DAY_DATE = startOfYear(NOW);
const MAX_CLOSING_DAY_DATE = endOfYear(addYears(NOW, 1));

const CREATE_CUSTOM_DEFAULTS: CustomClosingDayFormValues = {
  date: NOW,
  name: '',
  branchIds: null,
  recurring: false,
};

type CustomClosingDayModalProps =
  | {mode: 'createCustom'}
  | {
      mode: 'updateCustom';
      closingDayId: string;
    };

export function CustomClosingDayModal(props: CustomClosingDayModalProps) {
  const isEdit = props.mode === 'updateCustom';
  const {data: branchList, isLoading: isBranchListLoading} = useGetBranchListQuery();
  const {data: closingDay, isLoading: isClosingDayLoading} = useGetClosingDayQuery(
    props.mode === 'updateCustom' ? {closingDayId: props.closingDayId} : skipToken
  );
  const [createCustom] = useCreateCustomClosingDayMutation();
  const [updateCustom] = useUpdateCustomClosingDayMutation();

  const branchOptions = getOptionsFromBranches(branchList);

  const defaultValues =
    isEdit && isNotNil(closingDay)
      ? {
          date: parseDate(closingDay.date),
          name: closingDay.localName,
          branchIds: closingDay.branchIds,
          recurring: closingDay.recurring,
        }
      : CREATE_CUSTOM_DEFAULTS;

  const handleSubmit: FormSubmitHandler<CustomClosingDayFormValues> = (values) => {
    const payload = {
      ...values,
      date: getApiDateString(values.date),
    };

    const request = isEdit
      ? updateCustom({closingDayId: props.closingDayId, ...payload})
      : createCustom(payload);

    return request
      .unwrap()
      .then(() => {
        refreshDmsDatagrid(CLOSING_DAY_GRID_CODE);
        showNotification.success(
          i18n.t(`general.notifications.successfully${isEdit ? 'Updated' : 'Created'}`)
        );
        handleClose();
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isBranchListLoading || (isEdit && isClosingDayLoading)}>
      <Form
        defaultValues={defaultValues}
        experimentalZodSchema={customClosingDayFormSchema}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              type="date"
              control={control}
              name="date"
              label={i18n.t('general.labels.date')}
              isRequired
              minDate={MIN_CLOSING_DAY_DATE}
              maxDate={MAX_CLOSING_DAY_DATE}
            />
            <FormField
              type="text"
              control={control}
              name="name"
              label={i18n.t('general.labels.name')}
              isRequired
            />
            <FormField
              type="multiChoice"
              control={control}
              name="branchIds"
              label={i18n.t('entity.branch.labels.branches')}
              options={branchOptions}
              menuInPortal
            />
            <FormField
              type="checkbox"
              control={control}
              name="recurring"
              label={i18n.t('page.settings.labels.recurringAnnually')}
            />
            <Separator spacing={2} />
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={handleClose}
              />
              <FormButton
                control={control}
                title={i18n.t('general.actions.save')}
                type="submit"
                variant="primary"
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
